import React from 'react';
import CodeSnippet from './CodeSnippet';
import {
  DocContainer,
  Title,
  SubTitle,
  Paragraph,
  List,
  ListItem,
  OrderedList,
  OrderedListItem,
  Code,
} from './DocStyles';

const CriandoPaginasDoc: React.FC = () => {
  return (
    <DocContainer>
      <Title>Criando Novas Páginas no SmartEntrega</Title>

      <Paragraph>
        Este documento explica como criar novas páginas no projeto SmartEntrega,
        incluindo a criação do componente da página, a adição da rota e a
        integração com o sistema de autenticação.
      </Paragraph>

      <SubTitle>Visão Geral</SubTitle>
      <Paragraph>
        Para criar uma nova página no SmartEntrega, você precisa seguir estes
        passos:
      </Paragraph>
      <OrderedList>
        <OrderedListItem>Criar o componente da página</OrderedListItem>
        <OrderedListItem>Adicionar a rota para a página</OrderedListItem>
        <OrderedListItem>Proteger a rota, se necessário</OrderedListItem>
        <OrderedListItem>
          Adicionar um link de navegação para a página
        </OrderedListItem>
      </OrderedList>

      <SubTitle>1. Criando o Componente da Página</SubTitle>
      <Paragraph>
        As páginas são componentes React que representam uma tela completa da
        aplicação. Elas são armazenadas na pasta <Code>src/pages</Code>.
      </Paragraph>

      <SubTitle>Estrutura Básica de uma Página</SubTitle>
      <CodeSnippet
        code={`// src/pages/MinhaNovaPage.tsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../store';
// Importe outros componentes e hooks necessários

// Estilos para a página
const PageContainer = styled.div\`
  padding: 20px;
\`;

const PageTitle = styled.h1\`
  margin-bottom: 20px;
  font-size: 24px;
\`;

// Componente da página
const MinhaNovaPage: React.FC = () => {
  // Estado local
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  
  // Acesso ao Redux
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);
  
  // Efeito para carregar dados
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Lógica para buscar dados
        // const response = await api.fetchSomeData();
        // setData(response.data);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Renderização condicional para loading
  if (loading) {
    return <div>Carregando...</div>;
  }
  
  return (
    <PageContainer>
      <PageTitle>Minha Nova Página</PageTitle>
      
      {/* Conteúdo da página */}
      <div>
        {data.length > 0 ? (
          <ul>
            {data.map(item => (
              <li key={item.id}>{item.name}</li>
            ))}
          </ul>
        ) : (
          <p>Nenhum dado encontrado.</p>
        )}
      </div>
    </PageContainer>
  );
};

export default MinhaNovaPage;`}
      />

      <SubTitle>Página com Formulário</SubTitle>
      <Paragraph>Exemplo de uma página com formulário:</Paragraph>
      <CodeSnippet
        code={`// src/pages/FormularioExemplo.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
// Importe componentes de formulário

const FormContainer = styled.div\`
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
\`;

const Form = styled.form\`
  display: flex;
  flex-direction: column;
  gap: 15px;
\`;

const FormGroup = styled.div\`
  display: flex;
  flex-direction: column;
\`;

const Label = styled.label\`
  margin-bottom: 5px;
  font-weight: bold;
\`;

const Input = styled.input\`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
\`;

const Button = styled.button\`
  padding: 10px 15px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: #0069d9;
  }
\`;

const FormularioExemplo: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar erro quando o campo é alterado
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/\\S+@\\S+\\.\\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    
    if (!formData.telefone.trim()) {
      newErrors.telefone = 'Telefone é obrigatório';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      // Lógica para enviar dados do formulário
      // await api.createSomething(formData);
      
      // Redirecionar após sucesso
      navigate('/');
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
    }
  };
  
  return (
    <FormContainer>
      <h1>Formulário de Exemplo</h1>
      
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="nome">Nome</Label>
          <Input
            type="text"
            id="nome"
            name="nome"
            value={formData.nome}
            onChange={handleChange}
          />
          {errors.nome && <span style={{ color: 'red' }}>{errors.nome}</span>}
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="email">Email</Label>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
          />
          {errors.email && <span style={{ color: 'red' }}>{errors.email}</span>}
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="telefone">Telefone</Label>
          <Input
            type="tel"
            id="telefone"
            name="telefone"
            value={formData.telefone}
            onChange={handleChange}
          />
          {errors.telefone && <span style={{ color: 'red' }}>{errors.telefone}</span>}
        </FormGroup>
        
        <Button type="submit">Enviar</Button>
      </Form>
    </FormContainer>
  );
};

export default FormularioExemplo;`}
      />

      <SubTitle>2. Adicionando a Rota</SubTitle>
      <Paragraph>
        Depois de criar o componente da página, você precisa adicionar uma rota
        para ela no arquivo <Code>src/routes/index.tsx</Code>:
      </Paragraph>
      <CodeSnippet
        code={`// src/routes/index.tsx
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
// Importações existentes...
import MinhaNovaPage from '../pages/MinhaNovaPage';
import FormularioExemplo from '../pages/FormularioExemplo';

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Rotas públicas */}
        <Route path="/login" element={<Login />} />
        
        {/* Rotas protegidas com Layout */}
        <Route 
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          {/* Rotas existentes */}
          <Route path="/" element={<Dashboard />} />
          <Route path="/cadastros" element={<Cadastros />} />
          <Route path="/relatorios" element={<Relatorios />} />
          
          {/* Novas rotas */}
          <Route path="/minha-nova-pagina" element={<MinhaNovaPage />} />
          <Route path="/formulario-exemplo" element={<FormularioExemplo />} />
          
          {/* Rota de documentação - acessível apenas para usuários com codigoGrupoUsuario = 1 */}
          <Route 
            path="/documentacao" 
            element={
              <ProtectedAdminRoute>
                <Documentacao />
              </ProtectedAdminRoute>
            } 
          />
        </Route>
        
        {/* Rota para página não encontrada */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRoutes;`}
      />

      <SubTitle>3. Protegendo a Rota</SubTitle>
      <Paragraph>
        Se a página requer permissões especiais, você pode protegê-la usando o
        componente <Code>ProtectedAdminRoute</Code> ou criar um novo componente
        de proteção:
      </Paragraph>
      <SubTitle>Criando um Componente de Proteção Personalizado</SubTitle>
      <Paragraph>
        Se você precisa de um tipo específico de proteção para uma rota, pode
        criar um componente personalizado:
      </Paragraph>
      <CodeSnippet
        code={`// src/components/ProtectedManagerRoute.tsx
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from '../store';

interface ProtectedManagerRouteProps {
  children: React.ReactElement;
}

const ProtectedManagerRoute: React.FC<ProtectedManagerRouteProps> = ({ children }) => {
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  
  // Verifica se o usuário está autenticado e tem o código de grupo de gerente (exemplo: 2)
  if (!isAuthenticated || user?.codigoGrupoUsuario !== 2) {
    // Redireciona para a página inicial se não tiver permissão
    return <Navigate to="/" replace />;
  }
  
  // Renderiza o conteúdo protegido se tiver permissão
  return children;
};

export default ProtectedManagerRoute;`}
      />

      <Paragraph>Usando o componente de proteção personalizado:</Paragraph>
      <CodeSnippet
        code={`// src/routes/index.tsx
// ...
import ProtectedManagerRoute from '../components/ProtectedManagerRoute';
import GerenciarEquipe from '../pages/GerenciarEquipe';

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* ... outras rotas ... */}
        
        {/* Rota protegida para gerentes */}
        <Route 
          path="/gerenciar-equipe" 
          element={
            <ProtectedManagerRoute>
              <GerenciarEquipe />
            </ProtectedManagerRoute>
          } 
        />
        
        {/* ... outras rotas ... */}
      </Routes>
    </BrowserRouter>
  );
};`}
      />

      <SubTitle>4. Adicionando Link de Navegação</SubTitle>
      <Paragraph>
        Para permitir que os usuários acessem a nova página, adicione um link de
        navegação no componente de menu ou sidebar:
      </Paragraph>
      <CodeSnippet
        code={`// src/components/Sidebar.tsx
import React from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { useAppSelector } from '../store';

const SidebarContainer = styled.div\`
  width: 250px;
  background-color: #343a40;
  color: white;
  height: 100vh;
  padding-top: 20px;
\`;

const NavItem = styled(NavLink)\`
  display: block;
  padding: 10px 15px;
  color: rgba(255, 255, 255, 0.75);
  text-decoration: none;
  
  &:hover {
    color: white;
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  &.active {
    color: white;
    background-color: rgba(255, 255, 255, 0.2);
  }
\`;

const Sidebar: React.FC = () => {
  const { user } = useAppSelector(state => state.auth);
  const isAdmin = user?.codigoGrupoUsuario === 1;
  const isManager = user?.codigoGrupoUsuario === 2;
  
  return (
    <SidebarContainer>
      <NavItem to="/" end>Dashboard</NavItem>
      <NavItem to="/cadastros">Cadastros</NavItem>
      <NavItem to="/relatorios">Relatórios</NavItem>
      
      {/* Link para a nova página */}
      <NavItem to="/minha-nova-pagina">Minha Nova Página</NavItem>
      <NavItem to="/formulario-exemplo">Formulário</NavItem>
      
      {/* Links condicionais baseados em permissões */}
      {isManager && (
        <NavItem to="/gerenciar-equipe">Gerenciar Equipe</NavItem>
      )}
      
      {isAdmin && (
        <NavItem to="/documentacao">Documentação</NavItem>
      )}
    </SidebarContainer>
  );
};

export default Sidebar;`}
      />

      <SubTitle>5. Atualizando o Título da Página</SubTitle>
      <Paragraph>
        Para que o título da página seja exibido corretamente no layout,
        atualize a função <Code>pageTitle</Code> no componente{' '}
        <Code>AppLayout</Code>:
      </Paragraph>
      <CodeSnippet
        code={`// src/routes/index.tsx
const AppLayout = () => {
  const location = useLocation();
  
  const pageTitle = useMemo(() => {
    const path = location.pathname;
    
    switch (path) {
      case '/':
        return 'Home';
      case '/cadastros':
        return 'Cadastro de Usuários';
      case '/relatorios':
        return 'Relatórios';
      case '/documentacao':
        return 'Documentação';
      // Adicione os títulos para as novas páginas
      case '/minha-nova-pagina':
        return 'Minha Nova Página';
      case '/formulario-exemplo':
        return 'Formulário de Exemplo';
      case '/gerenciar-equipe':
        return 'Gerenciamento de Equipe';
      default:
        return 'SmartEntrega';
    }
  }, [location.pathname]);
  
  return (
    <Layout pageTitle={pageTitle}>
      <Outlet />
    </Layout>
  );
};`}
      />

      <SubTitle>6. Criando Páginas com Parâmetros de Rota</SubTitle>
      <Paragraph>
        Para páginas que precisam de parâmetros dinâmicos na URL (como IDs),
        você pode usar parâmetros de rota:
      </Paragraph>
      <CodeSnippet
        code={`// src/pages/DetalhesEntrega.tsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
// Importações...

const DetalhesEntrega: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [entrega, setEntrega] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchEntrega = async () => {
      try {
        setLoading(true);
        // Buscar dados da entrega usando o ID
        // const response = await api.entregas.getById(id);
        // setEntrega(response.data);
        setError(null);
      } catch (error) {
        console.error('Erro ao buscar detalhes da entrega:', error);
        setError('Não foi possível carregar os detalhes da entrega.');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchEntrega();
    }
  }, [id]);
  
  if (loading) return <div>Carregando...</div>;
  if (error) return <div>Erro: {error}</div>;
  if (!entrega) return <div>Entrega não encontrada.</div>;
  
  return (
    <div>
      <h1>Detalhes da Entrega #{id}</h1>
      
      <div>
        <h2>{entrega.titulo}</h2>
        <p>Status: {entrega.status}</p>
        <p>Cliente: {entrega.cliente}</p>
        <p>Endereço: {entrega.endereco}</p>
        <p>Data: {new Date(entrega.data).toLocaleDateString()}</p>
        
        {/* Mais detalhes... */}
      </div>
    </div>
  );
};

export default DetalhesEntrega;`}
      />

      <Paragraph>Adicionando a rota com parâmetro:</Paragraph>
      <CodeSnippet
        code={`// src/routes/index.tsx
// ...
import DetalhesEntrega from '../pages/DetalhesEntrega';

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* ... outras rotas ... */}
        
        {/* Rota com parâmetro */}
        <Route 
          path="/entregas/:id" 
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          } 
        >
          <Route index element={<DetalhesEntrega />} />
        </Route>
        
        {/* ... outras rotas ... */}
      </Routes>
    </BrowserRouter>
  );
};`}
      />

      <SubTitle>7. Páginas com Subrotas</SubTitle>
      <Paragraph>
        Para páginas mais complexas que têm suas próprias subrotas, você pode
        usar rotas aninhadas:
      </Paragraph>
      <CodeSnippet
        code={`// src/pages/Configuracoes/index.tsx
import React from 'react';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';
import ConfigMenu from './ConfigMenu';

const ConfigContainer = styled.div\`
  display: flex;
\`;

const MenuContainer = styled.div\`
  width: 250px;
  padding: 20px;
  background-color: #f5f5f5;
\`;

const ContentContainer = styled.div\`
  flex: 1;
  padding: 20px;
\`;

const Configuracoes: React.FC = () => {
  return (
    <ConfigContainer>
      <MenuContainer>
        <ConfigMenu />
      </MenuContainer>
      <ContentContainer>
        <Outlet /> {/* Aqui serão renderizadas as subrotas */}
      </ContentContainer>
    </ConfigContainer>
  );
};

export default Configuracoes;`}
      />

      <Paragraph>Componente de menu para navegação entre subrotas:</Paragraph>
      <CodeSnippet
        code={`// src/pages/Configuracoes/ConfigMenu.tsx
import React from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

const MenuList = styled.ul\`
  list-style: none;
  padding: 0;
  margin: 0;
\`;

const MenuItem = styled.li\`
  margin-bottom: 10px;
\`;

const MenuLink = styled(NavLink)\`
  display: block;
  padding: 8px 10px;
  color: #333;
  text-decoration: none;
  border-radius: 4px;
  
  &:hover {
    background-color: #e9ecef;
  }
  
  &.active {
    background-color: #007bff;
    color: white;
  }
\`;

const ConfigMenu: React.FC = () => {
  return (
    <MenuList>
      <MenuItem>
        <MenuLink to="/configuracoes/geral" end>Configurações Gerais</MenuLink>
      </MenuItem>
      <MenuItem>
        <MenuLink to="/configuracoes/perfil">Perfil</MenuLink>
      </MenuItem>
      <MenuItem>
        <MenuLink to="/configuracoes/notificacoes">Notificações</MenuLink>
      </MenuItem>
      <MenuItem>
        <MenuLink to="/configuracoes/seguranca">Segurança</MenuLink>
      </MenuItem>
    </MenuList>
  );
};

export default ConfigMenu;`}
      />

      <Paragraph>Componentes para as subrotas:</Paragraph>
      <CodeSnippet
        code={`// src/pages/Configuracoes/ConfigGeral.tsx
import React from 'react';

const ConfigGeral: React.FC = () => {
  return (
    <div>
      <h2>Configurações Gerais</h2>
      {/* Conteúdo... */}
    </div>
  );
};

export default ConfigGeral;

// src/pages/Configuracoes/ConfigPerfil.tsx
import React from 'react';

const ConfigPerfil: React.FC = () => {
  return (
    <div>
      <h2>Configurações de Perfil</h2>
      {/* Conteúdo... */}
    </div>
  );
};

export default ConfigPerfil;

// Outros componentes de configuração...`}
      />

      <Paragraph>Configurando as rotas aninhadas:</Paragraph>
      <CodeSnippet
        code={`// src/routes/index.tsx
// ...
import Configuracoes from '../pages/Configuracoes';
import ConfigGeral from '../pages/Configuracoes/ConfigGeral';
import ConfigPerfil from '../pages/Configuracoes/ConfigPerfil';
import ConfigNotificacoes from '../pages/Configuracoes/ConfigNotificacoes';
import ConfigSeguranca from '../pages/Configuracoes/ConfigSeguranca';

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* ... outras rotas ... */}
        
        {/* Rotas aninhadas para configurações */}
        <Route 
          path="/configuracoes" 
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route element={<Configuracoes />}>
            <Route index element={<Navigate to="geral" replace />} /> {/* Redireciona para a subrota padrão */}
            <Route path="geral" element={<ConfigGeral />} />
            <Route path="perfil" element={<ConfigPerfil />} />
            <Route path="notificacoes" element={<ConfigNotificacoes />} />
            <Route path="seguranca" element={<ConfigSeguranca />} />
          </Route>
        </Route>
        
        {/* ... outras rotas ... */}
      </Routes>
    </BrowserRouter>
  );
};`}
      />

      <SubTitle>Conclusão</SubTitle>
      <Paragraph>
        Criar novas páginas no SmartEntrega envolve vários passos, desde a
        criação do componente da página até a configuração das rotas e links de
        navegação. Seguindo este guia, você pode adicionar novas funcionalidades
        à aplicação de forma organizada e consistente.
      </Paragraph>
      <Paragraph>Lembre-se de considerar:</Paragraph>
      <List>
        <ListItem>
          A proteção adequada para a página (pública, autenticada, com
          permissões específicas)
        </ListItem>
        <ListItem>A integração com o sistema de navegação</ListItem>
        <ListItem>A atualização do título da página no layout</ListItem>
        <ListItem>A organização de páginas complexas com subrotas</ListItem>
      </List>
    </DocContainer>
  );
};

export default CriandoPaginasDoc;
